const EXTERNAL_USER_PROFILE_KEY = "externalUserProfile";

type UnknownObject = object;

const TOKEN_FIELDS = [
    "accessToken",
    "accessTokenExpiresAt",
    "refreshToken",
    "refreshTokenExpiresAt",
] as const;

function getLocalStorage(): Storage | null {
    if (typeof window === "undefined") {
        return null;
    }

    return window.localStorage;
}

export function sanitizeExternalUserProfile<T extends UnknownObject>(
    profile: T
): T {
    const sanitizedProfile = {
        ...(profile as Record<string, unknown>),
    };

    for (const field of TOKEN_FIELDS) {
        delete sanitizedProfile[field];
    }

    return sanitizedProfile as T;
}

export function getStoredExternalUserProfile<
    T extends UnknownObject,
>(): T | null {
    const storage = getLocalStorage();
    if (!storage) {
        return null;
    }

    try {
        const rawProfile = storage.getItem(EXTERNAL_USER_PROFILE_KEY);
        if (!rawProfile) {
            return null;
        }

        const parsedProfile = JSON.parse(rawProfile);
        if (!parsedProfile || typeof parsedProfile !== "object") {
            return null;
        }

        return parsedProfile as T;
    } catch {
        return null;
    }
}

export function setStoredExternalUserProfile(profile: UnknownObject): void {
    const storage = getLocalStorage();
    if (!storage) {
        return;
    }

    storage.setItem(
        EXTERNAL_USER_PROFILE_KEY,
        JSON.stringify(sanitizeExternalUserProfile(profile))
    );
}

export function clearStoredExternalUserProfile(): void {
    const storage = getLocalStorage();
    storage?.removeItem(EXTERNAL_USER_PROFILE_KEY);
}

export function hasStoredExternalUserProfile(): boolean {
    const storage = getLocalStorage();
    return Boolean(storage?.getItem(EXTERNAL_USER_PROFILE_KEY));
}
